import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { api } from '@/util/api';
import { DirectorChat } from '@/components/director/DirectorChat';
import { PrimitivesPalette, type PrimitiveDefinition } from '@/components/director/PrimitivesPalette';
import {
  DestinationPreview,
  type DestinationPreviewMetadata,
} from '@/components/event-viewer/destination/DestinationPreview';
import type { DirectorMode } from '@/hooks/useDirector';
import { EventWorkspaceShell } from '@/components/EventWorkspaceShell';
import { EventViewSwitcher } from '@/components/EventViewSwitcher';
import { SiteFooter } from '@/components/SiteFooter';
import { WorkspaceHeader } from '@/components/workspace/WorkspaceHeader';
import { workspaceError } from '@/lib/workspace';
import { BackLink, Button, EmptyState, IconButton, IconButtonLink, Notice, TextField, ToggleGroup, useToast } from '@/components/play';
import {
  Check,
  Edit2,
  Maximize2,
  Minimize2,
  RefreshCw,
  Eye,
  Rocket,
  PanelRightClose,
  PanelRight,
  MonitorPlay,
  Palette,
  Radio,
  Clapperboard,
  X,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────

interface EventDetails {
  id: string;
  type: string;
  attributes: {
    name: string;
    livekit_room_name?: string;
    event_page?: {
      id: string;
      status: 'draft' | 'preview' | 'published';
      version: number;
      preview_url?: string;
      published_url?: string;
    };
  };
}

// ─── Component ────────────────────────────────────────────────────

export function CanvasPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const location = useLocation();
  const { toast } = useToast();

  const routeState = location.state as
    | { initialPrompt?: string; mode?: DirectorMode }
    | null;

  const [event, setEvent] = useState<EventDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missingEvent, setMissingEvent] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  // Panel states
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [paletteCollapsed, setPaletteCollapsed] = useState(true);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const previewRefreshTimeoutRef = useRef<number | null>(null);
  const [destinationMetadata, setDestinationMetadata] =
    useState<DestinationPreviewMetadata | null>(null);

  // Chat input injection (for primitive suggestions)
  const [injectedPrompt, setInjectedPrompt] = useState<string | null>(null);
  const [pendingInitialPrompt, setPendingInitialPrompt] = useState<string | null>(() =>
    routeState?.initialPrompt?.trim() ? routeState.initialPrompt.trim() : null
  );
  const [directorMode, setDirectorMode] = useState<DirectorMode>(
    routeState?.mode ?? 'design'
  );

  const loadEvent = useCallback(async () => {
    if (!eventId) return;

    setLoading(true);
    setError(null);
    setMissingEvent(false);
    try {
      const res = await api.get<{ data: EventDetails }>(`/events/${eventId}`);
      setEvent(res.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setEvent(null);
      setMissingEvent(status === 404);
      setError(
        status === 404
          ? 'This event doesn’t exist, or you no longer have access to it.'
          : workspaceError(err, 'Try again in a moment.')
      );
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  // Fetch event details
  useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  useEffect(() => {
    if (event && !isEditingTitle) {
      setTitleDraft(event.attributes.name);
    }
  }, [event, isEditingTitle]);

  const liveUrl = useMemo(() => {
    if (!eventId) return '';
    return event?.attributes.event_page?.published_url || `${window.location.origin}/live/${eventId}`;
  }, [event, eventId]);

  // Handle primitive selection
  const handlePrimitiveSelect = useCallback((primitive: PrimitiveDefinition) => {
    setInjectedPrompt(primitive.example);
    setPaletteCollapsed(true);
  }, []);

  // Director mutations are persisted by Rails. Debounce one fresh snapshot so
  // a multi-tool turn does not thrash the shared destination runtime.
  const handleDestinationUpdate = useCallback(() => {
    if (previewRefreshTimeoutRef.current) {
      window.clearTimeout(previewRefreshTimeoutRef.current);
    }

    previewRefreshTimeoutRef.current = window.setTimeout(() => {
      setPreviewKey((k) => k + 1);
      previewRefreshTimeoutRef.current = null;
    }, 350);
  }, []);

  // Refresh preview
  const handleRefreshPreview = useCallback(() => {
    setPreviewKey((k) => k + 1);
  }, []);

  useEffect(() => {
    return () => {
      if (previewRefreshTimeoutRef.current) {
        window.clearTimeout(previewRefreshTimeoutRef.current);
      }
    };
  }, []);

  const handlePublish = useCallback(async () => {
    if (
      !eventId ||
      publishing ||
      !destinationMetadata ||
      destinationMetadata.draftRevision < 1
    ) {
      return;
    }

    setPublishing(true);
    try {
      await api.post(`/events/${eventId}/destination/publish`, {
        base_revision: destinationMetadata.draftRevision,
        idempotency_key: crypto.randomUUID(),
      });
      setPreviewKey((key) => key + 1);

      toast({
        title: 'Destination published',
        description: 'Viewers see the new layout.',
        tone: 'success',
      });
    } catch (err: unknown) {
      toast({
        title: 'Destination not published',
        description: workspaceError(err, 'Your draft is still saved. Try again.'),
        tone: 'error',
      });
    } finally {
      setPublishing(false);
    }
  }, [destinationMetadata, eventId, publishing, toast]);

  const handleStartTitleEdit = useCallback(() => {
    if (!event) return;
    setTitleDraft(event.attributes.name);
    setIsEditingTitle(true);
  }, [event]);

  const handleCancelTitleEdit = useCallback(() => {
    setTitleDraft(event?.attributes.name ?? '');
    setIsEditingTitle(false);
  }, [event]);

  const handleSaveTitle = useCallback(async () => {
    const nextTitle = titleDraft.trim();
    if (!eventId || !event || !nextTitle || nextTitle === event.attributes.name || savingTitle) {
      setIsEditingTitle(false);
      setTitleDraft(event?.attributes.name ?? '');
      return;
    }

    setSavingTitle(true);
    try {
      await api.patch(`/events/${eventId}`, { event: { name: nextTitle } });
      setEvent((current) =>
        current
          ? {
              ...current,
              attributes: {
                ...current.attributes,
                name: nextTitle,
              },
            }
          : current
      );
      setIsEditingTitle(false);
      toast({ title: 'Event renamed', tone: 'success' });
    } catch (err: unknown) {
      toast({
        title: 'Event not renamed',
        description: workspaceError(err, 'Your event keeps its name. Try again.'),
        tone: 'error',
      });
    } finally {
      setSavingTitle(false);
    }
  }, [event, eventId, savingTitle, titleDraft, toast]);

  // Loading state
  if (loading) {
    return (
      // The workspace's header over the screen the canvas will fill.
      <div className="play-theme flex h-screen flex-col overflow-hidden">
        <WorkspaceHeader label="Host workspace" />
        <main className="flex min-h-0 flex-1 items-center justify-center">
          <ContentSkeleton label="Loading Director" variant="viewer" className="max-w-7xl px-6" />
        </main>
      </div>
    );
  }

  // Error state
  if (missingEvent) {
    return (
      <div className="play-theme min-h-screen">
        <WorkspaceHeader label="Host workspace" />
        <main className="flex min-h-[60vh] flex-col gap-l px-l pt-l">
          <BackLink to="/app">Events</BackLink>
          <h1 className="text-xl font-medium text-neutral-10">AI Producer</h1>
          <EmptyState
            className="m-auto w-full max-w-lg"
            icon={<MonitorPlay />}
            title="Event not found"
            description={error}
            action={
              <Button onClick={() => void loadEvent()} variant="secondary">
                <RefreshCw aria-hidden="true" />
                Try again
              </Button>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="play-theme min-h-screen">
        <WorkspaceHeader label="Host workspace" />
        <main className="flex flex-col gap-l px-l pt-l">
          <BackLink to="/app">Events</BackLink>
          <h1 className="text-xl font-medium text-neutral-10">AI Producer</h1>
          <Notice
            tone="error"
            title="This event couldn’t load"
            action={
              <Button onClick={() => void loadEvent()} variant="secondary">
                <RefreshCw aria-hidden="true" />
                Try again
              </Button>
            }
          >
            {error || 'Try again in a moment.'}
          </Notice>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const hasDestinationDraft =
    (destinationMetadata?.draftRevision ?? 0) > 0;

  return (
    <EventWorkspaceShell
      backTo={`/events/${eventId}`}
      backLabel={event.attributes.name}
      title={
        <div className="flex items-center gap-2">
          {isEditingTitle ? (
            <>
              <TextField
                label="Event name"
                hideLabel
                size="compact"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleSaveTitle();
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    handleCancelTitleEdit();
                  }
                }}
                autoFocus
                className="w-[420px] max-w-[52vw]"
              />
              <IconButton
                label="Save event name"
                variant="ghost"
                size="compact"
                onClick={() => void handleSaveTitle()}
                loading={savingTitle}
                disabled={!titleDraft.trim()}
              >
                <Check />
              </IconButton>
              <IconButton
                label="Cancel renaming"
                variant="ghost"
                size="compact"
                onClick={handleCancelTitleEdit}
                disabled={savingTitle}
              >
                <X />
              </IconButton>
            </>
          ) : (
            <>
              <h1 className="truncate text-sm font-semibold text-neutral-10">
                {event.attributes.name}
              </h1>
              <IconButton
                label="Rename event"
                variant="ghost"
                size="compact"
                onClick={handleStartTitleEdit}
              >
                <Edit2 />
              </IconButton>
            </>
          )}
        </div>
      }
      meta={
        <>
          <span>{directorMode === 'studio' ? 'Studio' : 'Destination'}</span>
          {destinationMetadata && (
            <>
              <span className="text-neutral-6">•</span>
              <span className="text-neutral-7">
                live v{destinationMetadata.publishedRevision}
              </span>
              {hasDestinationDraft && (
                <>
                  <span className="text-neutral-6">•</span>
                  <span className="text-warning">
                    draft v{destinationMetadata.draftRevision}
                  </span>
                </>
              )}
            </>
          )}
        </>
      }
      actions={
        <>
          <IconButton
            label="Refresh destination preview"
            variant="ghost"
            size="compact"
            onClick={handleRefreshPreview}
          >
            <RefreshCw />
          </IconButton>
          <IconButton
            label={previewFullscreen ? 'Exit fullscreen preview' : 'Open fullscreen preview'}
            variant="ghost"
            size="compact"
            onClick={() => setPreviewFullscreen(!previewFullscreen)}
          >
            {previewFullscreen ? <Minimize2 /> : <Maximize2 />}
          </IconButton>

          <div className="mx-1 h-6 w-px bg-neutral-4" />

          <ToggleGroup
            label="Director mode"
            hideLabel
            iconOnly
            value={directorMode}
            onValueChange={setDirectorMode}
            items={[
              { value: 'stream', label: 'Stream setup', icon: <Radio /> },
              { value: 'design', label: 'Destination design', icon: <Palette /> },
              { value: 'studio', label: 'Live studio', icon: <Clapperboard /> },
            ]}
          />

          <div className="mx-1 h-6 w-px bg-neutral-4" />

          <Button
            size="compact"
            onClick={handlePublish}
            disabled={!hasDestinationDraft}
            loading={publishing}
          >
            <Rocket />
            Publish
          </Button>

          <IconButtonLink href={liveUrl} target="_blank" rel="noopener noreferrer" variant="ghost" size="compact" label="Open live event page in new tab">
            <Eye />
          </IconButtonLink>

          <div className="mx-1 h-6 w-px bg-neutral-4" />

          <IconButton
            label={chatCollapsed ? 'Open Director chat' : 'Collapse Director chat'}
            variant="ghost"
            size="compact"
            onClick={() => setChatCollapsed(!chatCollapsed)}
          >
            {chatCollapsed ? <PanelRight /> : <PanelRightClose />}
          </IconButton>
        </>
      }
      workspaceSwitcher={eventId ? <EventViewSwitcher eventId={eventId} active="director" /> : undefined}
      // A screen-high column, so the canvas fills what the headers leave whatever their height, and ends at the
      // screen's edge: no footer.
      className="flex h-screen flex-col overflow-hidden"
      contentClassName="flex min-h-0 flex-1 overflow-hidden"
      footer={false}
    >
      <div className="flex flex-1 overflow-hidden">
        {/* Preview Pane */}
        <div
          className={cn(
            'flex-1 flex flex-col bg-neutral-3 transition-all duration-[var(--motion-large-duration)]',
            previewFullscreen && 'fixed inset-0 z-50'
          )}
        >
          {!previewFullscreen && (
            <div className="border-b border-neutral-4 bg-neutral-2 px-5 py-2.5 text-xs text-neutral-7">
              {directorMode === 'studio'
                ? 'Studio uses the live component state and the same destination runtime viewers receive.'
                : 'Preview renders the private draft with real event, camera, tracking, and results context.'}
            </div>
          )}

          {/* The same provider/runtime graph mounted by the public viewer. */}
          <div className="relative flex-1 p-4">
            <div className="relative h-full overflow-auto rounded-2xl bg-neutral-1">
              <DestinationPreview
                eventId={eventId!}
                mode={directorMode === 'studio' ? 'studio' : 'preview'}
                refreshKey={previewKey}
                onLoaded={setDestinationMetadata}
              />
            </div>

            {/* Fullscreen exit hint */}
            {previewFullscreen && (
              <Button
                variant="secondary"
                size="compact"
                onClick={() => setPreviewFullscreen(false)}
                className="absolute top-4 right-4 z-10"
              >
                <Minimize2 aria-hidden="true" />
                Exit fullscreen
              </Button>
            )}
          </div>

          {/* Primitives Palette */}
          {!previewFullscreen && directorMode === 'design' && (
            <PrimitivesPalette
              onSelect={handlePrimitiveSelect}
              collapsed={paletteCollapsed}
              onToggleCollapse={() => setPaletteCollapsed(!paletteCollapsed)}
            />
          )}
        </div>

        {/* Chat Pane */}
        {!previewFullscreen && !chatCollapsed && (
          <div
            className={cn(
              'w-[420px] border-l border-neutral-4 bg-neutral-2 p-m flex flex-col',
              'transition-all duration-[var(--motion-large-duration)]'
            )}
          >
            <DirectorChat
              eventId={eventId!}
              mode={directorMode}
              className="flex-1"
              placeholder={
                directorMode === 'studio'
                  ? 'Direct the live destination…'
                  : directorMode === 'design'
                    ? 'Describe the destination…'
                    : 'Describe the stream setup…'
              }
              welcomeMessage={
                directorMode === 'studio'
                  ? `Direct ${event.attributes.name}`
                  : directorMode === 'design'
                    ? `Let's design ${event.attributes.name}`
                    : `Let's set up ${event.attributes.name}`
              }
              onWorkspaceUpdate={handleDestinationUpdate}
              initialPrompt={pendingInitialPrompt}
              onInitialPromptConsumed={() => setPendingInitialPrompt(null)}
              draftPrompt={injectedPrompt}
            />
          </div>
        )}

        {/* Collapsed chat rail: a Play icon button reopens the chat where it was. */}
        {!previewFullscreen && chatCollapsed && (
          <aside
            aria-label="Director chat, collapsed"
            className="flex w-12 flex-col items-center gap-xs border-l border-neutral-4 bg-neutral-2 py-m"
          >
            <IconButton label="Open Director chat" variant="ghost" onClick={() => setChatCollapsed(false)}>
              <MonitorPlay />
            </IconButton>
            <span aria-hidden="true" className="text-xs text-neutral-7 [writing-mode:vertical-lr] rotate-180">
              Director
            </span>
          </aside>
        )}
      </div>
    </EventWorkspaceShell>
  );
}

export default CanvasPage;
